// src/features/locations/OfficeRecordDrawer.jsx
//
// Task #9085 — Full office record drawer (680px, four tabs) over the Offices
// list. Route-owned: the owner passes locationId + tab from
// /places/offices/{id}?tab= and receives tab/paging/close intents back.
//
// Composition only. Every number comes from an existing reader:
//   • header strip seats/assigned/peak — the same definitions and canonical
//     reporting-month row selection the retired 400px peek (LocationDrawerV2)
//     used (computeAssignedPct, selectReportingMonthRow on the detail
//     utilization rows), so the strip inherits the peek's selection rule;
//   • Overview "Space fit" and Space "Capacity & occupancy" embed the full
//     page's SpaceFitOverviewPane / LocationOverviewCard, keeping
//     their own structural-snapshot captions and attendance-row selection
//     (office-overview-vintage-boundary: captions and selections are NOT
//     unified with the strip);
//   • Lease & cost is the existing LeaseAndCostPane (sensitivity-gated), whose
//     "What's ahead" card portals into the Overview tab;
//   • Projects is the existing OfficeProjectsActivityPane;
//   • /api/plan/capacity has no per-office demand series, so the projection
//     remains explicitly unavailable; only this office's dated commitments
//     are shown, never relabelled market totals.
//
// Unknown and archived IDs share one empty state (getLocation → null).
// Network/auth failures render a distinct, retryable error.
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UNSAFE_LocationContext, UNSAFE_NavigationContext } from "react-router-dom";
import { Building2, Pencil } from "lucide-react";
import { T, LAYERS } from "../../app/tokens.js";
import { SlideSheet } from "../../components/SlideSheet.jsx";
import { RecordDrawerHeader } from "../../components/RecordDrawerHeader.jsx";
import { RecordSectionTabs } from "../../components/RecordSectionTabs.jsx";
import { OfficeKeyFacts } from "./OfficeKeyFacts.jsx";
import { CoverBand } from "../../components/RecordCoverBand.jsx";
import { RecordReturnLink } from "../../components/RecordReturnLink.jsx";
import { showToast } from "../../components/toastService.jsx";
import { marketCapacitySummary, capacityHrefForMarket, upcomingCommitments } from "../plan/MarketRecordDrawer.jsx";
import { useCapacitySnapshot } from "../capacity/useCapacitySnapshot.js";
import { ProjectMediaSection } from "../projects/media/ProjectMediaSection.jsx";
import { officeMediaAdapter } from "../../data/locationMediaClient.js";
import { useOfficeCover } from "./useOfficeCover.js";
import { guardRouterNavigator, registerNavigationGuard, syncNavigationIndex, proceedConfirmedNavigation } from "../projects/recordNavigationGuard.js";
import { getLocation, updateStage, updateLocation, uploadLocationPhoto, deleteLocationPhoto } from "../../data/locationsClient.js";
import { listLocationLeases, getLeaseById } from "../../data/leasesClient.js";
import { EditLeaseModal } from "../leases/EditLeaseModal.jsx";
import { AuthError } from "../../data/errors.js";
import { buildPrefill, buildDiffPayload } from "./buildDiffPayload.js";
import { EditLocationModal } from "./EditLocationModal.jsx";
import { LocationOverviewCard } from "./LocationOverviewCard.jsx";
import { SpaceFitOverviewPane, VERDICT_RECOMMENDED } from "./SpaceFitOverviewPane.jsx";
import { LeaseAndCostPane } from "./LeaseAndCostPane.jsx";
import { OfficeProjectsActivityPane } from "./OfficeProjectsActivityPane.jsx";
import { computeAssignedPct, fmtPeakMonth } from "../../v2/locations/LocationDrawerV2.jsx";
import { computeSpaceFit, SPACE_FIT_COLOR, spaceFitShortAction } from "../../utils/spaceFitVerdict.js";
import { getAnalyticalSqft } from "../../utils/locationSqftDerived.js";
import { selectReportingMonthRow } from "../../services/reportingPeriod.js";
import { fmtDate, fmtFootprintSqft } from "../../utils/format.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { buildOfficeDrawerUrl, buildLeaseUrl, OFFICE_DRAWER_TABS } from "../../services/buildUrl.js";

export const OFFICE_UNAVAILABLE_COPY = "This office isn't available. It may have been archived or the link is wrong.";
export const RECENTLY_DELETED_HREF = "/admin?section=recently_deleted";
const TAB_LABEL = { overview: "Overview", space: "Space", lease: "Lease & cost", projects: "Projects", media: "Media" };
const INLINE_REQUIRED = ["market", "country", "region", "address"];
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const PANEL_SEL = "[data-testid='office-record-drawer']";

const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.cardShadow, minWidth: 0 };
const eyebrow = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".08em", textTransform: "uppercase", color: T.mid };
const ghostBtn = {
  display: "inline-flex", alignItems: "center", gap: 5, height: 28, padding: "0 10px", border: `1px solid ${T.border}`,
  borderRadius: 8, background: T.card, color: T.dark, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};
const linkBtn = { border: 0, background: "transparent", padding: 0, color: T.blue, fontSize: T.fsSm5 ?? T.fsSm, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit", textAlign: "left" };

// ─── Pure helpers (exported for tests) ──────────────────────────────────────

/** Header V2 identity `{City} · {alias}` — no premises; missing parts omitted, never invented. */
export function officeIdentityLine(loc) {
  const alias = loc?.locationAlias && loc.locationAlias !== loc?.market ? loc.locationAlias : null;
  return [loc?.market, alias].filter(Boolean).join(" · ");
}

export const OFFICE_HEADER_HEIGHT = 184;
export const OFFICE_STRIP_HEIGHT = 68;

/** Header V2 title parts: `{City} · {alias}` (no premises). Missing parts are
 *  omitted, never invented; `full` is the complete identity for hover. */
export function officeTitleParts(loc) {
  const city = loc?.market ? String(loc.market).trim() : "";
  const rawAlias = loc?.locationAlias ? String(loc.locationAlias).trim() : "";
  const alias = rawAlias && rawAlias !== city ? rawAlias : "";
  const premises = String(loc?.premises || loc?.suite || "").trim();
  const full = [city, alias, premises].filter(Boolean).join(" · ") || (loc?.id ?? "");
  return { city, alias, full };
}

/** Header V2 meta: `{street}, {city}` without repeating a city already in the address. */
export function officeAddressLine(loc) {
  const address = String(loc?.address || "").trim();
  const city = String(loc?.market || "").trim();
  if (!address) return city;
  const street = address.split(",")[0].trim();
  if (!city || street.toLowerCase() === city.toLowerCase()) return street;
  return `${street}, ${city}`;
}

/** Header strip figures, using the retired peek's definitions verbatim. */
export function officeStripFigures(loc) {
  if (!loc) return null;
  const installed = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const displayed = loc.displayInstalledCapacity != null ? Number(loc.displayInstalledCapacity) : installed;
  const assigned = loc.latestHeadcount?.totalCount != null ? Number(loc.latestHeadcount.totalCount) : null;
  const assignedPct = computeAssignedPct(loc);
  const rows = Array.isArray(loc.utilization) ? loc.utilization : [];
  const row = selectReportingMonthRow(rows, loc.reportingPeriod);
  const peakBadges = row?.peakDailyUniqueBadges != null ? Number(row.peakDailyUniqueBadges) : null;
  const peakCount = peakBadges != null && Number.isFinite(peakBadges) ? Math.round(peakBadges) : null;
  const peakPct = peakBadges != null && installed != null && installed > 0 ? (peakBadges / installed) * 100 : null;
  const { verdict } = computeSpaceFit(getAnalyticalSqft(loc), installed, assigned);
  const maxCap = loc.maxCapacity != null ? Number(loc.maxCapacity) : null;
  return {
    installed, displayed, assigned, assignedPct, peakCount, peakPct, peakMonth: row?.month ?? null,
    peakSourceLabel: row?.sourceLabel ?? null,
    emptyAtPeak: installed != null && peakCount != null ? Math.max(0, installed - peakCount) : null,
    unassigned: installed != null && assigned != null ? installed - assigned : null,
    expansion: installed != null && maxCap != null ? maxCap - installed : null,
    maxCap, verdict, recommendation: verdict ? VERDICT_RECOMMENDED[verdict] ?? null : null,
    shortAction: spaceFitShortAction(verdict),
  };
}

/** Plan disposition for this office from the existing location_plans payload. */
export function officePlanRow(planData, loc) {
  const markets = Array.isArray(planData) ? planData : Array.isArray(planData?.markets) ? planData.markets : [];
  for (const m of markets) {
    const row = (m?.locations ?? []).find((l) => l?.locationId === loc?.id);
    if (row) return { ...row, marketId: m.id ?? m.marketId ?? null, marketName: m.name ?? m.market ?? null };
  }
  return null;
}

/** Primary lease, same rule as the retired peek. */
export function primaryLeaseOf(leases) {
  if (!Array.isArray(leases)) return null;
  return leases.find((l) => l.status === "active" || l.status === "holdover") ?? leases[0] ?? null;
}

const fmtN = (n) => (n == null || !Number.isFinite(n) ? "–" : Math.round(n).toLocaleString("en-US"));
const fmtPct = (n) => (n == null || !Number.isFinite(n) ? null : `${Math.round(n)}%`);

// ─── Pieces ─────────────────────────────────────────────────────────────────

const oneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };

function StripCell({ label, value, sub, testId, accent, onClick, ariaLabel }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} data-testid={testId} onClick={onClick} aria-label={ariaLabel}
      className={onClick ? "ord-strip-btn" : undefined}
      style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: OFFICE_STRIP_HEIGHT, boxSizing: "border-box", padding: "0 14px", minWidth: 0,
        borderLeft: `1px solid ${T.hairline ?? T.border}`, borderTop: 0, borderRight: 0, borderBottom: 0, background: "transparent", textAlign: "left", font: "inherit", cursor: onClick ? "pointer" : undefined }}>
      <div style={{ ...eyebrow, ...oneLine, lineHeight: "14px" }}>{label}</div>
      <div title={typeof value === "string" ? value : undefined} style={{ ...oneLine, marginTop: 3, fontSize: T.fsLg, lineHeight: "22px", fontWeight: T.fwBold, color: accent ?? T.dark, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div title={sub || undefined} style={{ ...oneLine, marginTop: 1, fontSize: T.fsXxs, lineHeight: "14px", color: T.mid }}>{sub || "\u00a0"}</div>
    </Tag>
  );
}

function CardHead({ title, action, accent = T.blue }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px 0" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        <span aria-hidden="true" style={{ width: 3, height: 14, borderRadius: 2, background: accent }} />
        {title}
      </h3>
      {action}
    </div>
  );
}

function Row({ label, children, testId }) {
  return (
    <div data-testid={testId} style={{ display: "grid", gridTemplateColumns: "112px minmax(0,1fr)", gap: 12, padding: "7px 0", borderTop: `1px solid ${T.hairline ?? T.border}`, fontSize: T.fsSm, alignItems: "baseline" }}>
      <span style={{ color: T.mid, fontWeight: T.fwSemibold }}>{label}</span>
      <span style={{ color: T.dark, minWidth: 0, overflowWrap: "anywhere" }}>{children}</span>
    </div>
  );
}

function SkeletonBody() {
  const blk = (h, w = "100%") => <div style={{ height: h, width: w, borderRadius: 8, background: T.lightGray, marginBottom: 10 }} />;
  return (
    <div data-testid="office-record-loading" aria-busy="true" aria-label="Loading office">
      {blk(14, "22%")}{blk(26, "58%")}{blk(12, "40%")}{blk(64)}{blk(180)}{blk(120)}
    </div>
  );
}

export function OfficeUnavailable({ isAdmin, onNavigate }) {
  return (
    <div data-testid="office-record-unavailable" style={{ ...card, padding: "36px 28px", marginTop: 24, textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 12, margin: "0 auto 14px", display: "grid", placeItems: "center", background: T.lightGray, color: T.mid }}>
        <Building2 size={22} />
      </div>
      <p style={{ margin: 0, fontSize: T.fsBase, color: T.dark, fontWeight: T.fwSemibold, lineHeight: 1.45 }}>{OFFICE_UNAVAILABLE_COPY}</p>
      {isAdmin && (
        <button type="button" data-testid="office-record-recently-deleted" onClick={() => onNavigate(RECENTLY_DELETED_HREF)}
          style={{ ...linkBtn, marginTop: 14, fontSize: T.fsSm }}>
          Open Recently Deleted →
        </button>
      )}
    </div>
  );
}

function OfficeLoadError({ error, onRetry }) {
  const auth = error instanceof AuthError || error?.status === 401 || error?.status === 403;
  return (
    <div role="alert" data-testid={auth ? "office-record-auth-error" : "office-record-error"} style={{ ...card, padding: "22px 22px", marginTop: 24 }}>
      <p style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {auth ? "You don't have access to this office right now." : "This office couldn't be loaded."}
      </p>
      <p style={{ margin: "6px 0 14px", fontSize: T.fsSm, color: T.mid }}>
        {auth ? "Your session may have expired or your role changed. Sign in again, then retry." : "The request failed before the record arrived. Check your connection and try again."}
      </p>
      {error?.message && (
        <p data-testid="office-record-error-message" style={{ margin: "0 0 14px", fontSize: T.fsSm, color: T.dark, fontFamily: T.fontMono || "monospace", overflowWrap: "anywhere" }}>{error.message}</p>
      )}
      <button type="button" data-testid="office-record-retry" onClick={onRetry} style={ghostBtn}>Retry</button>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

export function OfficeRecordDrawer({
  locationId, tab = "overview", onTabChange, onClose, orderedIds = [], onNavigate, navigate,
  currentRole = "viewer", planData = [], onOpenFull, onOpenMarket, onOpenProject, onOfficeSaved,
  recordReturn = null, onFollowReturn, onOpenLease, userKey = null,
  leaseId = null,
}) {
  const open = Boolean(locationId);
  const activeTab = OFFICE_DRAWER_TABS.includes(tab) ? tab : "overview";
  const canWrite = currentRole === "admin" || currentRole === "editor";
  const isAdmin = currentRole === "admin";
  const sensitivity = useSensitivityPolicy();
  const canSeeLeaseCost = sensitivity.canSee("offices.lease_cost_pane");
  const { flags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const projectsNavEnabled = flagsReady && governedNavEnabled(flags, nav_config, "projects", nav_order);
  const planNavEnabled = flagsReady && governedNavEnabled(flags, nav_config, "plan", nav_order);

  const [load, setLoad] = useState({ status: "loading", loc: null, error: null, id: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [leases, setLeases] = useState(null); // null loading | [] | false error
  const [mediaCount, setMediaCount] = useState(null);
  const reportMediaCount = useCallback(state => setMediaCount({
    id: locationId, userKey, role: currentRole, count: state.status === "ok" ? state.count : null,
  }), [locationId, userKey, currentRole]);
  useEffect(() => { setMediaCount(null); }, [locationId, userKey, currentRole]);
  const [areasEdit, setAreasEdit] = useState(null);
  useEffect(() => { setAreasEdit(null); }, [locationId]);
  const [aheadEl, setAheadEl] = useState(null);
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 680 : Math.min(680, window.innerWidth)));
  const bodyRef = useRef(null);
  const [viewerHost, setViewerHost] = useState(null);
  useEffect(() => { setViewerHost(bodyRef.current?.closest(PANEL_SEL) ?? null); }, [open]);
  const [mediaEditor, setMediaEditor] = useState({ editing: false, busy: false });
  const mediaEditorRef = useRef(mediaEditor);
  const [leaveIntent, setLeaveIntent] = useState(null);
  const [mediaReset, setMediaReset] = useState(0);
  const otherEditor = useRef({ open: false, dirty: false, busy: false });
  const routerLocation = useContext(UNSAFE_LocationContext)?.location;
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;
  const identity = `${userKey ?? "session"}:${currentRole}`;
  const reportMediaEditor = useCallback(st => {
    mediaEditorRef.current = st;
    setMediaEditor(st);
  }, []);
  useEffect(() => {
    mediaEditorRef.current = { editing: false, busy: false };
    setMediaEditor(mediaEditorRef.current); setLeaveIntent(null);
  }, [locationId, identity]);
  const guardMedia = useCallback(proceed => {
    if (otherEditor.current.open) {
      showToast("Save or cancel the open office or lease editor before leaving. An in-progress save must finish first.", { type: "error" });
      return;
    }
    const st = mediaEditorRef.current;
    if (st.requestLeave) st.requestLeave(proceed);
    else if (st.busy || st.editing) setLeaveIntent(() => proceed);
    else proceed();
  }, []);
  useEffect(() => open ? guardRouterNavigator(navigator) : undefined, [open, navigator]);
  useEffect(() => {
    syncNavigationIndex();
    const media = mediaEditorRef.current;
    if (media.transient && !media.editing && !media.busy) media.requestLeave?.(() => {});
  }, [routerLocation?.key]);
  useEffect(() => {
    if (!open) return undefined;
    const off = registerNavigationGuard({
      isBlocking: () => Boolean(otherEditor.current.open || mediaEditorRef.current.busy || mediaEditorRef.current.editing),
      onBlocked: proceed => guardMedia(proceed),
    });
    const before = e => { if (otherEditor.current.dirty || otherEditor.current.busy || mediaEditorRef.current.uploading || mediaEditorRef.current.editing) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", before);
    return () => { off(); window.removeEventListener("beforeunload", before); };
  }, [open, guardMedia]);
  const returnFocus = useRef(null);
  const [capacity, reloadCapacity] = useCapacitySnapshot(open && activeTab === "space");

  // Edit office (fields + photo only; schedules stay on the full page).
  const [edit, setEdit] = useState({ open: false, draft: null, errors: {}, formError: "", submitting: false, noChanges: false, photo: null });

  useEffect(() => {
    if (!locationId) return undefined;
    let live = true;
    setLoad((s) => (s.id === locationId && s.status === "ok" ? s : { status: "loading", loc: null, error: null, id: locationId }));
    Promise.resolve().then(() => getLocation(locationId))
      .then((loc) => { if (live) setLoad(loc ? { status: "ok", loc, error: null, id: locationId } : { status: "missing", loc: null, error: null, id: locationId }); })
      .catch((error) => { if (live) setLoad({ status: "error", loc: null, error, id: locationId }); });
    return () => { live = false; };
  }, [locationId, reloadToken]);

  useEffect(() => {
    if (!locationId || load.status !== "ok") return undefined;
    let live = true;
    setLeases(null);
    Promise.resolve().then(() => listLocationLeases(locationId))
      .then((rows) => { if (live) setLeases(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (live) setLeases(false); });
    return () => { live = false; };
  }, [locationId, load.status, reloadToken]);

  // Shell: dialog semantics, focus return, resize.
  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    const t = setTimeout(() => {
      const el = bodyRef.current?.closest(PANEL_SEL);
      if (el) { el.setAttribute("role", "dialog"); el.setAttribute("aria-modal", "true"); el.setAttribute("aria-labelledby", "office-record-title"); }
      bodyRef.current?.querySelector("[data-testid='office-record-title']")?.focus();
    }, 0);
    const onResize = () => setWidth(Math.min(680, window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      const el = returnFocus.current;
      setTimeout(() => { if (el && el.isConnected && typeof el.focus === "function") el.focus({ preventScroll: true }); }, 0);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const panel = bodyRef.current?.closest(PANEL_SEL);
      if (!panel || edit.open) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((n) => !n.closest("[hidden]"));
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      const cur = document.activeElement;
      if (!items.includes(cur)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && cur === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && cur === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, edit.open]);

  const loc = load.status === "ok" ? load.loc : null;
  const fig = useMemo(() => officeStripFigures(loc), [loc]);
  const plan = useMemo(() => (planNavEnabled && loc ? officePlanRow(planData, loc) : null), [planNavEnabled, planData, loc]);
  const lease = Array.isArray(leases) ? primaryLeaseOf(leases) : null;
  const selectedLease = leaseId && Array.isArray(leases)
    ? leases.find((entry) => entry.id === leaseId) ?? null : lease;
  const cover = useOfficeCover(locationId, Boolean(loc), identity, loc?.imageUrl, reloadToken);
  const marketId = loc?.marketId ?? plan?.marketId ?? null;
  const capSummary = useMemo(() => (marketId ? marketCapacitySummary(capacity, marketId) : { status: "excluded" }), [capacity, marketId]);
  const officeCommitments = useMemo(() => (loc && capSummary.status === "ok"
    ? upcomingCommitments(capSummary, [{ locationId: loc.id, locationAlias: loc.locationAlias, onlineDate: loc.onlineDate, closeDate: loc.closeDate }])
      .filter((c) => c.locationId === loc.id)
    : []), [capSummary, loc]);

  const requestClose = () => { if (!edit.open && !areasEdit) guardMedia(() => onClose?.()); };
  const requestTab = key => guardMedia(() => onTabChange?.(key));
  const editLeaseAreas = async (id) => {
    if (!canWrite) return;
    const officeId = loc.id;
    try {
      const fullLease = await getLeaseById(id);
      if (!fullLease) throw new Error("This lease isn't available.");
      setAreasEdit({ lease: fullLease, officeId });
    } catch {
      showToast("Couldn't load lease areas. Try again.", { type: "error" });
    }
  };
  const go = useCallback((to) => { if (typeof navigate === "function") navigate(to); }, [navigate]);

  // ── Edit flow (mirrors LocationDetail's field + photo save) ───────────────
  const openEdit = () => loc && setEdit({ open: true, draft: buildPrefill(loc), errors: {}, formError: "", submitting: false, noChanges: false, photo: null });
  const closeEdit = () => setEdit((s) => (s.submitting ? s : { ...s, open: false, draft: null, photo: null }));
  const editDirty = Boolean(edit.open && loc && edit.draft && (edit.photo !== null || Object.keys(buildDiffPayload(loc, edit.draft)).length > 0));
  otherEditor.current = { open: edit.open || Boolean(areasEdit), dirty: editDirty || Boolean(areasEdit), busy: edit.submitting };
  const saveEdit = async () => {
    if (!loc || !edit.draft || edit.submitting) return;
    const errors = {};
    for (const f of INLINE_REQUIRED) if (!edit.draft[f] || String(edit.draft[f]).trim() === "") errors[f] = "Required.";
    if (Object.keys(errors).length) { setEdit((s) => ({ ...s, errors })); return; }
    const payload = buildDiffPayload(loc, edit.draft);
    const fields = Object.keys(payload).length > 0;
    if (!fields && edit.photo === null) { setEdit((s) => ({ ...s, noChanges: true })); return; }
    setEdit((s) => ({ ...s, submitting: true, errors: {}, formError: "", noChanges: false }));
    try {
      if (edit.photo instanceof File) await uploadLocationPhoto(loc.id, edit.photo);
      else if (edit.photo === "DELETE") await deleteLocationPhoto(loc.id);
      if (fields) await updateLocation(loc.id, payload);
      setEdit({ open: false, draft: null, errors: {}, formError: "", submitting: false, noChanges: false, photo: null });
      showToast("Changes saved.", { type: "success" });
      setReloadToken((n) => n + 1);
      onOfficeSaved?.(loc.id, payload);
    } catch (err) {
      const fieldErrors = err?.fieldErrors && typeof err.fieldErrors === "object" ? err.fieldErrors : {};
      setEdit((s) => ({ ...s, submitting: false, errors: fieldErrors, formError: err?.message || "Couldn't save changes. Try again." }));
    }
  };
  const changeStage = async (next) => {
    if (!loc) return;
    await updateStage(loc.id, next);
    showToast("Stage updated.", { type: "success" });
    setReloadToken((n) => n + 1);
    onOfficeSaved?.(loc.id, { stage: next });
  };

  const titleParts = officeTitleParts(loc);
  const addressLine = loc ? officeAddressLine(loc) : "";
  const copyPath = locationId ? buildOfficeDrawerUrl(locationId, activeTab, routerLocation?.search ?? "") : null;

  return (
    <SlideSheet open={open} onClose={requestClose} width={width} layer={LAYERS.stickyChrome + 1}
      panelTestId="office-record-drawer" scrimTestId="office-record-drawer-scrim" closeTestId="office-record-drawer-close" hideCloseButton>
      <style>{`
        [data-testid="office-record-drawer"] .ord-tab { position: relative; border: 0; background: transparent; padding: 10px 2px; margin-right: 18px; font: inherit; font-size: ${T.fsSm}px; font-weight: ${T.fwBold}; color: ${T.mid}; cursor: pointer; }
        [data-testid="office-record-drawer"] .ord-tab[aria-selected="true"] { color: ${T.dark}; }
        [data-testid="office-record-drawer"] .ord-tab::after { content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 2px; border-radius: 2px; background: ${T.blue}; transform: scaleX(0); transition: transform .18s ease; }
        [data-testid="office-record-drawer"] .ord-tab[aria-selected="true"]::after { transform: scaleX(1); }
        [data-testid="office-record-drawer"] .ord-tab:focus-visible { outline: 2px solid ${T.blue}; outline-offset: 2px; border-radius: 4px; }
        [data-testid="office-record-drawer"] .ord-panel { animation: ordIn .22s ease both; }
        @keyframes ordIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { [data-testid="office-record-drawer"] .ord-panel { animation: none; } [data-testid="office-record-drawer"] .ord-tab::after { transition: none; } }
        [data-testid="office-record-cover"] { margin: -18px -22px 0; }
        [data-testid="office-record-drawer"] .ord-strip-btn:hover { background: ${T.tint || T.lightGray} !important; }
        [data-testid="office-record-drawer"] .ord-strip-btn:focus-visible { outline: 2px solid ${T.blue}; outline-offset: -2px; }
        .ord-strip { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); }
        .ord-strip > :first-child { border-left: 0 !important; }
        .ord-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px; }
        @media (max-width: 560px) {
          .ord-grid2 { grid-template-columns: minmax(0,1fr); }
          [data-testid="office-record-body"] { padding-left: 14px !important; padding-right: 14px !important; }
          [data-testid="office-record-cover"] { margin: -18px -14px 0; }
        }
      `}</style>
      <div ref={bodyRef} data-testid="office-record-body" style={{ padding: "18px 22px 44px", boxSizing: "border-box", minHeight: "100%", background: T.tint || T.lightGray }}>
        <div data-testid="office-record-cover">
          <CoverBand cover={cover} minHeight={OFFICE_HEADER_HEIGHT} marginBottom={0}>
            <RecordDrawerHeader testId="office-record-header" height={OFFICE_HEADER_HEIGHT} stripHeight={OFFICE_STRIP_HEIGHT}
              padding="14px 22px 0"
              eyebrow="Office" eyebrowColor={cover ? T.dark : T.mid}
              returnLink={recordReturn && <RecordReturnLink inline entry={recordReturn} navigate={(to, state) => guardMedia(() => onFollowReturn?.(to, state))} />}
              titleId="office-record-title" titleTestId="office-record-title"
              titleText={loc ? titleParts.full : undefined}
              title={loc
                ? (titleParts.city || titleParts.alias
                  ? <>{titleParts.city}{titleParts.city && titleParts.alias ? " · " : ""}{titleParts.alias && <span data-testid="office-record-alias" style={{ color: T.mid, fontWeight: T.fwSemibold }}>{titleParts.alias}</span>}</>
                  : loc.id)
                : load.status === "loading" ? "Loading office…" : "Office"}
              meta={loc
                ? <><span data-testid="office-record-id" style={{ fontFamily: "inherit", fontWeight: T.fwSemibold, color: T.planCompFill ?? T.dark }}>{loc.id}</span>
                    {addressLine && <><span aria-hidden="true"> · </span><span data-testid="office-record-address">{addressLine}</span></>}</>
                : "\u00a0"}
              metaText={loc ? [loc.id, loc.address || addressLine].filter(Boolean).join(" · ") : undefined}
              metaTestId="office-record-meta" metaColor={cover ? T.dark : T.mid}
              share={copyPath ? { path: copyPath, testId: "office-record-copy-link" } : null}
              fullPage={loc && onOpenFull ? { onClick: () => guardMedia(() => onOpenFull(loc.id)), label: "Open full page", testId: "office-record-full-page" } : null}
              pager={{ ids: orderedIds, currentId: locationId, onNavigate: (id) => guardMedia(() => onNavigate?.(id)),
                keyboardActive: open && !edit.open, disabled: edit.open || !onNavigate, showUnavailable: true,
                previousLabel: "Previous office", nextLabel: "Next office",
                previousTestId: "office-record-prev", nextTestId: "office-record-next", positionTestId: "office-record-position" }}
              onClose={requestClose} closeTestId="office-record-drawer-close" closeLabel="Close office"
              strip={(
                <div data-testid="office-record-strip" className="ord-strip" aria-busy={load.status === "loading" || undefined}
                  style={{ ...card, height: OFFICE_STRIP_HEIGHT, boxSizing: "border-box", overflow: "hidden", background: cover ? T.coverPanelBg ?? T.card : T.card, backdropFilter: cover ? T.coverPanelBlur : undefined }}>
                  {fig ? (
                    <>
                      <StripCell testId="strip-installed" label="Installed seats" value={fmtN(fig.displayed)}
                        sub={fig.displayed == null ? "Not recorded" : fig.maxCap != null ? `max ${fmtN(fig.maxCap)}` : "no max set"} />
                      <StripCell testId="strip-assigned" label="Assigned" value={fmtN(fig.assigned)}
                        sub={fig.assigned == null ? "No headcount" : fmtPct(fig.assignedPct) ? `${fmtPct(fig.assignedPct)} of seats` : "seats unknown"} />
                      <StripCell testId="strip-peak" label="Peak day" value={fmtN(fig.peakCount)}
                        sub={fig.peakCount == null ? "No attendance this month" : [fmtPct(fig.peakPct) ? `${fmtPct(fig.peakPct)} of seats` : "seats unknown", fig.peakMonth && fmtPeakMonth(fig.peakMonth)].filter(Boolean).join(" · ")} />
                      <StripCell testId="strip-space-fit" label="Space fit" value={fig.verdict ?? "–"} accent={fig.verdict ? SPACE_FIT_COLOR[fig.verdict] : T.light}
                        sub={fig.shortAction} onClick={() => requestTab("space")}
                        ariaLabel={`Space fit: ${fig.verdict ?? "unavailable"}, ${fig.shortAction}. Open Space tab`} />
                    </>
                  ) : ["Installed seats", "Assigned", "Peak day", "Space fit"].map((label) => (
                    <StripCell key={label} label={label} value={load.status === "loading" ? <span aria-hidden="true" style={{ display: "inline-block", width: 44, height: 14, borderRadius: 4, background: T.lightGray }} /> : "–"} />
                  ))}
                </div>
              )} />
          </CoverBand>
        </div>

        {load.status === "loading" && <div style={{ marginTop: 18 }}><SkeletonBody /></div>}
        {load.status === "missing" && <OfficeUnavailable isAdmin={isAdmin} onNavigate={go} />}
        {load.status === "error" && <OfficeLoadError error={load.error} onRetry={() => setReloadToken((n) => n + 1)} />}

        {loc && (
          <>
            {leaveIntent && <div role="alert" data-testid="office-media-navigation-guard" style={{ ...card, padding: 12, marginTop: 14, fontSize: T.fsSm }}>
              <p>{mediaEditor.busy ? "An office media change is still saving. Wait for it to finish before leaving." : "Media has an open editor. Leaving discards unsaved changes."}</p>
              <button type="button" style={ghostBtn} onClick={() => setLeaveIntent(null)}>Keep editing</button>{" "}
              <button type="button" style={ghostBtn} disabled={mediaEditor.busy} onClick={() => {
                if (mediaEditorRef.current.busy) return;
                const proceed = leaveIntent;
                mediaEditorRef.current = { editing: false, busy: false }; setMediaEditor(mediaEditorRef.current);
                setMediaReset(n => n + 1); setLeaveIntent(null);
                proceedConfirmedNavigation(proceed);
              }}>Discard and leave</button>
            </div>}
            <RecordSectionTabs tabs={OFFICE_DRAWER_TABS.map(key => ({ key, label: TAB_LABEL[key],
              count: key === "lease" && Array.isArray(leases) ? leases.length
                : key === "projects" && projectsNavEnabled && Array.isArray(loc.projects) ? loc.projects.length
                : key === "media" && mediaCount?.id === locationId && mediaCount?.userKey === userKey && mediaCount?.role === currentRole ? mediaCount.count : null }))}
              active={activeTab} onChange={requestTab} label="Office record"
              tabId={key => `office-tab-${key}`} panelId={key => `office-panel-${key}`} tabTestId={key => `office-record-tab-${key}`} />

            {/* Overview */}
            <section role="tabpanel" id="office-panel-overview" aria-labelledby="office-tab-overview" hidden={activeTab !== "overview"}
              className="ord-panel" data-testid="office-panel-overview" style={{ display: activeTab === "overview" ? "grid" : "none", gap: 14, marginTop: 14 }}>
              <OfficeKeyFacts loc={loc} lease={lease} leases={leases} plan={plan} planNavEnabled={planNavEnabled}
                canWrite={canWrite} onStageChange={changeStage}
                onOpenMarket={loc.market && marketId && onOpenMarket ? () => guardMedia(() => onOpenMarket(marketId, loc)) : undefined}
                onOpenLease={lease ? () => guardMedia(() => onOpenLease ? onOpenLease(lease.id, loc) : go(buildLeaseUrl(lease.id))) : undefined}
                editAction={canWrite && (
                  <button type="button" data-testid="office-record-edit" onClick={openEdit} style={ghostBtn}><Pencil size={12} aria-hidden="true" /> Edit office</button>
                )} />

              {canSeeLeaseCost && <div ref={setAheadEl} data-testid="office-overview-ahead" />}
            </section>

            {/* Space */}
            <section role="tabpanel" id="office-panel-space" aria-labelledby="office-tab-space" hidden={activeTab !== "space"}
              className="ord-panel" data-testid="office-panel-space" style={{ display: activeTab === "space" ? "grid" : "none", gap: 14, marginTop: 14 }}>
              {activeTab === "space" && (
                <>
                  <div style={card} data-testid="space-fit-card">
                    <SpaceFitOverviewPane location={loc} latestHeadcount={loc.latestHeadcount ?? null} badgeIntegrationPending={!!loc.badgeIntegrationPending} showScatter={false} />
                  </div>

                  <div style={card} data-testid="space-occupancy-card">
                    <LocationOverviewCard location={loc} latestHeadcount={loc.latestHeadcount ?? null} locationId={loc.id} badgeIntegrationPending={!!loc.badgeIntegrationPending} />
                  </div>

                  <div style={card} data-testid="space-capacity-card">
                    <CardHead title="Capacity · this office" accent={T.ragAmber}
                      action={marketId && <button type="button" data-testid="space-open-capacity" style={linkBtn} onClick={() => go(capacityHrefForMarket(marketId))}>Open in Capacity →</button>} />
                    <div style={{ padding: "10px 16px 14px" }}>
                      {!marketId ? (
                        <div data-testid="space-capacity-no-market" style={{ fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>This office isn't linked to a market, so it isn't in the Capacity model.</div>
                      ) : capSummary.status === "loading" ? (
                        <div data-testid="space-capacity-loading" aria-busy="true" style={{ height: 40, borderRadius: 6, background: T.lightGray }} />
                      ) : capSummary.status === "error" ? (
                        <div role="alert" data-testid="space-capacity-error" style={{ display: "flex", gap: 10, alignItems: "center", fontSize: T.fsSm, color: T.mid }}>
                          <span style={{ flex: 1 }}>Capacity is unavailable: {capSummary.error || "the capacity read failed."}</span>
                          <button type="button" style={ghostBtn} onClick={reloadCapacity}>Retry</button>
                        </div>
                      ) : capSummary.status === "excluded" ? (
                        <div data-testid="space-capacity-excluded" style={{ fontSize: T.fsSm, color: T.mid, fontStyle: "italic" }}>This office's market is not in the Capacity model.</div>
                      ) : (
                        <>
                          {/* Task #9085 evidence gap: GET /api/plan/capacity returns market-level demand and
                              usable-seat cells only (server/services/capacityReadModel.js); demand is never
                              allocated to offices, so no office-scoped 24-month series exists. Do not
                              substitute the market strip — state the gap and show this office's own schedule. */}
                          <div role="status" data-testid="space-office-series-unavailable" style={{ fontSize: T.fsSm, color: T.mid, padding: "8px 10px", borderRadius: 6, background: T.lightGray }}>
                            <strong style={{ color: T.dark }}>Office projection unavailable.</strong> Capacity projects demand per market, not per office, so there is no 24-month series for this office. Open {loc.market || "the market"} in Capacity for the market projection.
                          </div>
                          <div style={{ marginTop: 12, ...eyebrow }}>This office's scheduled changes</div>
                          {officeCommitments.length === 0
                            ? <div data-testid="space-office-commitments-empty" style={{ marginTop: 4, fontSize: T.fsSm, color: T.light, fontStyle: "italic" }}>No dated seat changes in the next 24 months.</div>
                            : <ul data-testid="space-office-commitments" style={{ listStyle: "none", margin: "4px 0 0", padding: 0 }}>
                                {officeCommitments.map((c) => (
                                  <li key={c.id ?? `${c.effectiveDate}-${c.delta}`} style={{ display: "flex", gap: 10, fontSize: T.fsSm, padding: "3px 0" }}>
                                    <span style={{ color: T.mid, minWidth: 92 }}>{fmtDate(c.effectiveDate)}</span>
                                    <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>
                                      {c.eventKind ?? "Seat change"}{Number.isFinite(c.delta) ? ` · ${c.delta > 0 ? "+" : ""}${c.delta} seats` : ""}
                                    </span>
                                  </li>
                                ))}
                              </ul>}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </section>

            {/* Lease & cost — kept mounted so What's ahead can portal into Overview. */}
            <section role="tabpanel" id="office-panel-lease" aria-labelledby="office-tab-lease" hidden={activeTab !== "lease"}
              className="ord-panel" data-testid="office-panel-lease" style={{ display: activeTab === "lease" ? "block" : "none", marginTop: 14 }}>
              {canSeeLeaseCost ? (
                <div style={{ ...card, padding: 0 }}>
                  <LeaseAndCostPane key={`${loc.id}:${reloadToken}`} location={loc} activeLease={selectedLease} projectDecisions={[]} aheadPortalTarget={aheadEl}
                    onEditAreas={canWrite ? editLeaseAreas : undefined}
                    onOpenLease={(id) => onOpenLease ? onOpenLease(id, loc) : go(buildLeaseUrl(id))} />
                </div>
              ) : (
                <div data-testid="office-lease-gated" style={{ ...card, padding: "20px 18px", fontSize: T.fsSm, color: T.mid }}>
                  Lease and cost details aren't available for your role.
                </div>
              )}
            </section>

            {/* Projects */}
            <section role="tabpanel" id="office-panel-projects" aria-labelledby="office-tab-projects" hidden={activeTab !== "projects"}
              className="ord-panel" data-testid="office-panel-projects" style={{ display: activeTab === "projects" ? "block" : "none", marginTop: 14 }}>
              {activeTab === "projects" && (projectsNavEnabled ? (
                <OfficeProjectsActivityPane locationId={loc.id} onOpenProject={onOpenProject ? (projectId) => onOpenProject(projectId, loc) : undefined}
                  onViewAll={() => go(`/projects?locationId=${encodeURIComponent(loc.id)}`)} />
              ) : (
                <div data-testid="office-projects-gated" style={{ ...card, padding: "20px 18px", fontSize: T.fsSm, color: T.mid }}>Projects aren't enabled for this workspace.</div>
              ))}
            </section>
            <section role="tabpanel" id="office-panel-media" aria-labelledby="office-tab-media" hidden={activeTab !== "media"}
              className="ord-panel" data-testid="office-panel-media" style={{ display: activeTab === "media" ? "block" : "none", marginTop: 14 }}>
              <ProjectMediaSection key={`${identity}:${loc.id}:${mediaReset}`} ownerId={loc.id} userKey={identity}
                mediaClient={officeMediaAdapter} allowManage={canWrite} presentation="drawer" hidden={activeTab !== "media"}
                viewerHost={viewerHost} onEditorState={reportMediaEditor} onStateChange={reportMediaCount} title="Media" />
            </section>
          </>
        )}
      </div>

      {canWrite && canSeeLeaseCost && areasEdit && loc && areasEdit.officeId === loc.id && (
        <EditLeaseModal isOpen lease={areasEdit.lease} initialField="areas" canWrite={canWrite} isAdmin={isAdmin}
          onClose={() => setAreasEdit(null)}
          onSaved={() => { setAreasEdit(null); setReloadToken((n) => n + 1); onOfficeSaved?.(loc.id, {}); }}
          onDeleted={() => { setAreasEdit(null); setReloadToken((n) => n + 1); onOfficeSaved?.(loc.id, {}); }} />
      )}
      {loc && edit.open && (
        <EditLocationModal
          isOpen={edit.open}
          canArchive={false}
          onClose={closeEdit}
          onSave={saveEdit}
          isDirty={editDirty}
          location={loc}
          editDraft={edit.draft}
          editErrors={edit.errors}
          editFormError={edit.formError}
          editSubmitting={edit.submitting}
          editNoChanges={edit.noChanges}
          onDraftChange={(field, value) => setEdit((s) => ({ ...s, draft: s.draft ? { ...s.draft, [field]: value } : s.draft, errors: { ...s.errors, [field]: undefined }, formError: "", noChanges: false }))}
          editPhotoPending={edit.photo}
          onEditPhotoChange={(photo) => setEdit((s) => ({ ...s, photo }))}
        />
      )}
    </SlideSheet>
  );
}

export default OfficeRecordDrawer;
